import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Container, Spinner } from "react-bootstrap";
import { CheckCircle2, FileCheck2, Lock, LogIn, Maximize2, PenLine, ShieldCheck } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import api from "Common/api";
import apiUrl from "slices/apiEndpoint";

const GuestHomeownerContractSign: React.FC = () => {
  const relibuildLogo = "https://tradesmen.s3.amazonaws.com/relibuild-logo/relibuild-logo.png";
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const signatureRef = useRef<SignatureCanvas | null>(null);
  const signatureContainerRef = useRef<HTMLDivElement | null>(null);
  const [contract, setContract] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [signatureWidth, setSignatureWidth] = useState(0);

  const tokenPath = encodeURIComponent(token);
  const pdfUrl = `${apiUrl}/contract/hic-contracts/guest/${tokenPath}/pdf`;

  useEffect(() => {
    if (!token) {
      setError("This signing link is incomplete.");
      setLoading(false);
      return;
    }
    api.get(`/contract/hic-contracts/guest/${tokenPath}`)
      .then((response: any) => setContract(response?.data))
      .catch((requestError: any) => {
        setError(requestError?.response?.data?.message || "This secure signing link is invalid or has expired.");
      })
      .finally(() => setLoading(false));
  }, [token, tokenPath]);

  useEffect(() => {
    const container = signatureContainerRef.current;
    if (!container || !contract?.canSign) return;

    const syncCanvasWidth = () => {
      const nextWidth = Math.floor(container.getBoundingClientRect().width);
      setSignatureWidth((currentWidth) => Math.abs(currentWidth - nextWidth) > 2 ? nextWidth : currentWidth);
    };
    syncCanvasWidth();
    const observer = new ResizeObserver(syncCanvasWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, [contract?.canSign]);

  const submitSignature = async () => {
    if (!accepted) {
      toast.warning("Please confirm that you reviewed and agree to the contract.");
      return;
    }
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.warning("Please draw your signature first.");
      return;
    }

    setSigning(true);
    try {
      const signatureBlob = await fetch(signatureRef.current.toDataURL("image/png"))
        .then((response) => response.blob());
      const body = new FormData();
      body.append("file", signatureBlob, "signature.png");
      const response: any = await api.post(
        `/contract/hic-contracts/guest/${tokenPath}/sign`,
        body,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setContract(response?.data);
      toast.success(response?.message || "Contract signed successfully.");
    } catch (requestError: any) {
      toast.error(requestError?.response?.data?.message || "Unable to sign the contract.");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light"><Spinner animation="border" /></div>;
  }

  if (error) {
    return (
      <Container className="py-5" style={{ maxWidth: 720 }}>
        <Alert variant="danger" className="rounded-4 border-0 shadow-sm">
          <h5 className="mb-2">Signing link unavailable</h5>
          <div>{error}</div>
        </Alert>
      </Container>
    );
  }

  const contractorLabel = contract?.contractorCompanyName || contract?.contractorName || "Your contractor";

  return (
    <div className="homeowner-guest-sign contract-flow-page min-vh-100">
      <style>{`
        .homeowner-guest-sign { background:#f3f6f8; color:#24374a; }
        .hoc-topbar { background:#fff; border-bottom:1px solid #dfe6eb; }
        .hoc-shell { max-width:1440px; }
        .hoc-logo { display:block; width:190px; max-width:46vw; height:52px; object-fit:contain; object-position:left center; }
        .hoc-header-actions { display:flex; align-items:center; gap:14px; }
        .hoc-login-action { display:inline-flex; align-items:center; justify-content:center; gap:7px; min-height:39px; padding:0 15px; border:1px solid #b9c7d2; border-radius:999px; background:#fff; color:#294a68; font-size:.84rem; font-weight:700; text-decoration:none; white-space:nowrap; transition:background-color .18s ease,border-color .18s ease,box-shadow .18s ease; }
        .hoc-login-action:hover { background:#edf3f7; border-color:#8ea4b5; color:#203f5b; box-shadow:0 5px 16px rgba(47,82,114,.1); }
        .hoc-hero { padding:32px 0 24px; }
        .hoc-eyebrow { color:#2f5272; font-size:.75rem; font-weight:800; letter-spacing:.12em; text-transform:uppercase; }
        .hoc-title { letter-spacing:-.035em; color:#24374a; }
        .hoc-card { border:1px solid #dfe6eb!important; border-radius:18px!important; box-shadow:0 14px 38px rgba(30,50,70,.07)!important; overflow:hidden; }
        .hoc-pdf-frame { width:100%; height:72vh; min-height:620px; border:0; background:#e8edf1; }
        .hoc-sign-card { position:sticky; top:22px; }
        .hoc-sign-head { padding:23px 24px; background:linear-gradient(135deg,#294a68,#2f5272); color:#fff; }
        .hoc-canvas { width:100%; height:177px; overflow:hidden; border:1.5px dashed #aebdca; border-radius:12px; background:#fff; touch-action:none; }
        .hoc-canvas canvas { display:block; cursor:crosshair; touch-action:none; }
        .hoc-primary { background:#2f5272!important; border-color:#2f5272!important; }
        .hoc-consent { display:flex; align-items:flex-start; gap:14px; margin-top:18px; padding:17px 16px; border:1.5px solid #9fc6ff; border-radius:14px; background:#eef5ff; color:#17233a; font-size:.92rem; line-height:1.55; cursor:pointer; transition:border-color .18s ease,background-color .18s ease,box-shadow .18s ease; }
        .hoc-consent:hover { border-color:#6da8fa; background:#e8f2ff; }
        .hoc-consent:focus-within { border-color:#3988f5; box-shadow:0 0 0 3px rgba(57,136,245,.14); }
        .hoc-consent input { width:21px; height:21px; margin:2px 0 0; flex:0 0 auto; cursor:pointer; accent-color:#1677ed; }
        .hoc-consent strong { color:#101b30; }
        .hoc-pdf-action { display:inline-flex!important; align-items:center; justify-content:center; gap:8px; width:auto!important; min-width:172px; height:40px; padding:0 16px!important; border:1px solid #b9c7d2; border-radius:999px; background:#fff; color:#294a68; font-size:.86rem; font-weight:700; line-height:1; text-decoration:none; white-space:nowrap; transition:background-color .18s ease,border-color .18s ease,box-shadow .18s ease,transform .18s ease; }
        .hoc-pdf-action:hover { background:#edf3f7; border-color:#8ea4b5; color:#203f5b; box-shadow:0 6px 18px rgba(47,82,114,.12); transform:translateY(-1px); }
        .hoc-pdf-action:focus-visible { outline:3px solid rgba(47,82,114,.22); outline-offset:2px; }
        .hoc-secure-note { background:#edf5f3; border:1px solid #d3e7e1; border-radius:12px; color:#345c53; }
        @media (max-width:991px) { .hoc-sign-card { position:static; } .hoc-pdf-frame { height:65vh; min-height:500px; } }
        @media (max-width:575px) { .hoc-logo { width:145px; height:42px; } .hoc-header-actions { gap:8px; } .hoc-secure-label { display:none; } .hoc-login-action { min-height:37px; padding:0 12px; } .hoc-pdf-action { min-width:148px; height:38px; padding:0 13px!important; } }
      `}</style>

      <div className="hoc-topbar py-3">
        <Container fluid className="hoc-shell px-3 px-lg-4 d-flex align-items-center justify-content-between">
          <div>
            <img className="hoc-logo" src={relibuildLogo} alt="RELiBUILD" />
          </div>
          <div className="hoc-header-actions">
            <div className="d-flex align-items-center gap-2 text-success fw-semibold small"><ShieldCheck size={18} /><span className="hoc-secure-label">Private &amp; secure</span></div>
            <Link to="/login" className="hoc-login-action" aria-label="Log in to your RELiBUILD homeowner account">
              <LogIn size={16} aria-hidden="true" />
              <span>Homeowner login</span>
            </Link>
          </div>
        </Container>
      </div>

      <Container fluid className="hoc-shell px-3 px-lg-4 pb-5">
        <div className="hoc-hero">
          <div className="hoc-eyebrow mb-2"><Lock size={13} className="me-2" />Homeowner signature request</div>
          <h1 className="hoc-title h2 fw-bold mb-2">Review and sign your home improvement contract</h1>
          <p className="text-muted mb-0">
            {contractorLabel} has signed the contract for <strong>{contract?.projectName}</strong>. Read the complete PDF before signing.
          </p>
        </div>

        <div className="row g-4 align-items-start">
          <div className="col-lg-8">
            <Card className="hoc-card border-0">
              <Card.Header className="bg-white border-bottom p-3 px-md-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                  <FileCheck2 className="text-primary" size={22} />
                  <div><strong>Contract PDF</strong><small className="d-block text-muted">{contract?.projectAddress || contract?.projectName}</small></div>
                </div>
                <a href={pdfUrl} target="_blank" rel="noreferrer" className="hoc-pdf-action" aria-label="Open contract PDF full screen">
                  <Maximize2 size={16} aria-hidden="true" />
                  <span>Open full screen</span>
                </a>
              </Card.Header>
              <Card.Body className="p-0">
                <iframe className="hoc-pdf-frame" src={pdfUrl} title="RELiBUILD home improvement contract PDF" />
              </Card.Body>
            </Card>
          </div>

          <div className="col-lg-4">
            <Card className="hoc-card hoc-sign-card border-0">
              <div className="hoc-sign-head">
                <div className="d-flex align-items-center gap-2 mb-2"><PenLine size={20} /><span className="fw-semibold">Homeowner signature</span></div>
                <div className="fs-5 fw-bold">{contract?.homeownerName || "Homeowner"}</div>
                <small className="text-white-50">Contract #{contract?.contractId}</small>
              </div>
              <Card.Body className="p-4">
                {contract?.canSign ? (
                  <>
                    <div className="hoc-secure-note d-flex align-items-start gap-2 p-3 small mb-4">
                      <ShieldCheck size={18} className="flex-shrink-0 mt-1" />
                      <span>Your signature will be securely recorded and added to the final contract PDF.</span>
                    </div>
                    <label className="fw-semibold small mb-2">Draw your signature</label>
                    <div className="hoc-canvas" ref={signatureContainerRef}>
                      {signatureWidth > 0 ? (
                        <SignatureCanvas
                          key={signatureWidth}
                          ref={signatureRef}
                          penColor="black"
                          backgroundColor="white"
                          canvasProps={{
                            width: signatureWidth,
                            height: 175,
                            "aria-label": "Draw your signature",
                          }}
                        />
                      ) : null}
                    </div>
                    <Button variant="link" size="sm" className="px-0 text-muted" onClick={() => signatureRef.current?.clear()}>Clear and redraw</Button>
                    <label className="hoc-consent">
                      <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
                      <span>
                        <strong>I have reviewed the complete contract PDF</strong> and agree to its terms and conditions. I understand that by signing, I am entering into a legally binding agreement with {contractorLabel}.
                      </span>
                    </label>
                    <Button className="hoc-primary w-100 rounded-pill py-3 mt-4 fw-semibold" disabled={signing} onClick={submitSignature}>
                      {signing ? <><Spinner size="sm" className="me-2" />Finalizing contract…</> : "Agree & sign contract"}
                    </Button>
                    <p className="text-muted text-center mt-3 mb-0" style={{ fontSize: 12 }}>
                      This private link is single-use and expires automatically.
                    </p>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <CheckCircle2 size={58} className="text-success mb-3" />
                    <h3 className="h5 fw-bold">Contract signed</h3>
                    <p className="text-muted mb-0">Your signature has been securely recorded and the final contract is being prepared.</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default GuestHomeownerContractSign;
